import { Box as MuiBox, styled } from "@mui/material";

import { Box } from "@/Components/v2/atoms/Box";
import { Typography } from "@/Components/v2/atoms/Typography";

import { LIKELIHOOD_LEVELS, LikelihoodLevel } from "../../helpers/likelihood";

const InfoCard = styled(Box)(({ theme }) => ({
  borderRadius: "4px",
  padding: 12,
  textAlign: "left",
  border: theme.palette.mode === "light" ? "1px solid #E7E7E7" : "1px solid #656565",
  boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.05)",
  backgroundColor: theme.palette.mode === "light" ? "#ffffff" : "#000000",
  width: "250px",
}));

const ProgressBar = styled(Box)({
  height: "12px",
  overflow: "hidden",
  width: "100%",
  display: "grid",
  gridTemplateColumns: "50% 30% 20%",
});

const ProgressSegment = styled(MuiBox)<{ color: string; active: boolean }>(({ color, active }) => ({
  backgroundColor: color,
  opacity: active ? 1 : 0.1,
  height: "100%",
}));

const LabelContainer = styled(Box)({
  position: "relative",
  height: "20px",
  marginTop: "8px",
});

const PercentageLabel = styled(Typography)<{
  $align?: "start" | "middle" | "end";
}>(({ theme, $align = "middle" }) => ({
  position: "absolute",
  bottom: 0,
  lineHeight: "16px",
  fontSize: "10px",
  fontWeight: 400,
  letterSpacing: 0.5,
  color: theme.palette.text.secondary,
  transform:
    $align === "start"
      ? "translateX(0%)"
      : $align === "end"
        ? "translateX(-100%)"
        : "translateX(-50%)",
}));

const ConnectingLine = styled(MuiBox)(({ theme }) => ({
  position: "absolute",
  width: "1px",
  backgroundColor: theme.palette.mode === "light" ? "#6E6972" : "#EBEBEB",
  zIndex: 5,
}));

interface LikelihoodChartProps {
  likelihoodPercentage: number;
  level: LikelihoodLevel;
  color: string;
  description: string;
  recommendation: string;
  containerWidth: number;
}

export const LikelihoodProgress = ({
  likelihoodPercentage,
  level,
  color,
  description,
  recommendation,
  containerWidth,
}: LikelihoodChartProps) => {
  const infoCardWidth = 280;
  const linePositionPercent = Math.max(0, Math.min(100, likelihoodPercentage));

  let cardLeftPercent = 50; // Default center
  if (containerWidth > 0) {
    const halfCardPx = infoCardWidth / 2;
    const minPercent = (halfCardPx / containerWidth) * 100;
    const maxPercent = 100 - minPercent;
    cardLeftPercent = Math.max(minPercent, Math.min(linePositionPercent, maxPercent));
  }

  return (
    <>
      {/* Wrapper for InfoCard to handle horizontal positioning */}
      <Box
        sx={{
          width: `${infoCardWidth}px`,
          ml: `${cardLeftPercent}%`,
          transform: "translateX(-50%)",
          visibility: containerWidth > 0 ? "visible" : "hidden",
          mb: 4,
        }}
      >
        <InfoCard>
          <Typography
            sx={{
              color,
              fontSize: 24,
              fontWeight: 600,
              lineHeight: "100%",
              mb: 1,
            }}
          >
            {likelihoodPercentage}%
          </Typography>
          <Typography
            variant="body2"
            sx={{ color, fontSize: 12, fontWeight: 600, letterSpacing: 0.5 }}
          >
            {level} Likelihood
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", fontSize: 10, mb: 1.5, letterSpacing: 0.5 }}
          >
            {description}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", fontSize: 10, lineHeight: 1.5, letterSpacing: 0.5 }}
          >
            {recommendation}
          </Typography>
        </InfoCard>
      </Box>

      {/* Wrapper for Progress Bar and Line */}
      <Box sx={{ position: "relative" }}>
        <ConnectingLine
          sx={{
            left: `${linePositionPercent}%`,
            transform: "translateX(-50%)",
            top: "-32px", // Pull it up into the margin of the card wrapper
            height: "32px",
            visibility: containerWidth > 0 ? "visible" : "hidden",
          }}
        />
        <ProgressBar>
          {Object.values(LIKELIHOOD_LEVELS).map((l) => (
            <ProgressSegment key={l.level} color={l.color} active={l.level === level} />
          ))}
        </ProgressBar>
        <LabelContainer>
          {["0%", "25%", "50%", "75%", "100%"].map((label) => {
            const pos = parseInt(label, 10);
            let align: "start" | "middle" | "end" = "middle";
            if (pos === 0) {
              align = "start";
            } else if (pos === 100) {
              align = "end";
            }

            return (
              <PercentageLabel key={label} $align={align} sx={{ left: `${pos}%` }}>
                {label}
              </PercentageLabel>
            );
          })}
        </LabelContainer>
      </Box>
    </>
  );
};
